import type {
  AttachInvoiceUserFilesMutationOptions,
  AttachInvoiceUserFilesVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import { attachInvoiceUserFiles } from './attachInvoiceUserFiles';
import { zodAttachInvoiceUserFilesResponseSchema } from './validations';

/**
 * Attaches staged user files and synchronizes the invoice detail cache.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns Mutation state and the batch attachment function.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/attach_user_file_invoices__invoice_id__user_files_post
 */
export function useMutationAttachInvoiceUserFiles(
  options?: AttachInvoiceUserFilesMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (variables: AttachInvoiceUserFilesVariables) =>
      attachInvoiceUserFiles(variables),
    async onSettled(data, error, variables, onMutateResult, context) {
      await queryClient.invalidateQueries({
        queryKey: queryKeysInvoice.getDetail(variables.invoiceId),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
    onSuccess(data, variables, onMutateResult, context) {
      const parsedResponse =
        zodAttachInvoiceUserFilesResponseSchema.safeParse(data);

      if (parsedResponse.success) {
        queryClient.setQueryData(
          queryKeysInvoice.getDetail(variables.invoiceId),
          parsedResponse.data.invoice,
        );
      }

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
